import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { FontIcon } from "@odl/core";

interface ContributionItem {
  date: string;
  title: string;
  description: string;
}

interface ContributionPhase {
  phase: string;
  items: ContributionItem[];
}

interface TeamMember {
  id: string;
  name: string;
  role: string;
  contributions: number;
  percentage: number;
  focus: string[];
  quote: string;
  contributionPhases: ContributionPhase[];
}

interface TeamMemberCardProps {
  member: TeamMember;
}

const phaseColors: Record<string, string> = {
  Foundation: "#1976D2",
  "Multi-Tenancy": "#7B1FA2",
  "Enterprise-Grade Features": "#00897B",
  "Production Excellence": "#C62828",
};

export function TeamMemberCard({ member }: TeamMemberCardProps) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          cursor: "pointer",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: 4,
            borderColor: "primary.main",
          },
        }}
        onClick={() => setExpanded(true)}
      >
        <CardContent
          sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
        >
          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 2 }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: "primary.main",
                fontSize: "1.25rem",
                fontWeight: 600,
              }}
            >
              {member.name
                .split(" ")
                .map((n) => n[0])
                .join("")}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h6"
                component="h3"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {member.name}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {member.role}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 2 }}>
            {member.focus.map((area) => (
              <Chip
                key={area}
                label={area}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            ))}
          </Box>

          <Typography
            variant="body2"
            sx={{
              mb: 2,
              fontStyle: "italic",
              borderLeft: 2,
              borderLeftColor: "grey.500",
              pl: 1.5,
              color: "text.secondary",
            }}
          >
            {member.quote}
          </Typography>

          <Box sx={{ mt: "auto" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "primary.main" }}
              >
                {member.contributions}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                commits ({member.percentage}%)
              </Typography>
            </Box>

            {/* Phase summary pills */}
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1 }}>
              {member.contributionPhases.map((p) => (
                <Chip
                  key={p.phase}
                  label={p.phase}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    bgcolor: phaseColors[p.phase] || "grey.500",
                    color: "white",
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
              }}
            >
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                View contribution timeline
              </Typography>
              <FontIcon className="i-navigation-chevron-down" />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={expanded}
        onClose={() => setExpanded(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2, maxHeight: "90vh" },
        }}
      >
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1 }}
        >
          <Avatar
            sx={{
              width: 64,
              height: 64,
              bgcolor: "primary.main",
              fontSize: "1.5rem",
              fontWeight: 600,
            }}
          >
            {member.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, color: "text.primary" }}
            >
              {member.name}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {member.role}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mr: 2 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              {member.contributions}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              commits
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(false)} size="small">
            <FontIcon className="i-navigation-close" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
            {member.focus.map((area) => (
              <Chip key={area} label={area} size="small" variant="outlined" />
            ))}
          </Box>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              fontStyle: "italic",
              borderLeft: 3,
              borderLeftColor: "primary.main",
              pl: 2,
              color: "text.secondary",
            }}
          >
            {member.quote}
          </Typography>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}
          >
            Contribution Timeline
          </Typography>

          {/* Phase sections with scrollable content */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              maxHeight: "60vh",
              overflowY: "auto",
              pr: 1,
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "grey.400",
                borderRadius: 3,
              },
            }}
          >
            {member.contributionPhases.map((phase) => (
              <Box key={phase.phase}>
                {/* Phase header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      bgcolor: phaseColors[phase.phase] || "grey.500",
                    }}
                  />
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: phaseColors[phase.phase] || "text.primary",
                    }}
                  >
                    {phase.phase}
                  </Typography>
                </Box>

                {/* Timeline items */}
                <Box sx={{ pl: 3 }}>
                  {phase.items.map((item, itemIndex) => (
                    <Box
                      key={itemIndex}
                      sx={{
                        position: "relative",
                        pb: itemIndex < phase.items.length - 1 ? 2 : 0,
                      }}
                    >
                      {/* Connecting line */}
                      {itemIndex < phase.items.length - 1 && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: -3,
                            top: 12,
                            width: 2,
                            height: "100%",
                            bgcolor: "divider",
                          }}
                        />
                      )}

                      {/* Timeline dot */}
                      <Box
                        sx={{
                          position: "absolute",
                          left: -7,
                          top: 6,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "background.paper",
                          border: 2,
                          borderColor:
                            phaseColors[phase.phase] || "primary.main",
                        }}
                      />

                      {/* Content card */}
                      <Card
                        variant="outlined"
                        sx={{
                          ml: 1,
                          borderLeft: 2,
                          borderLeftColor:
                            phaseColors[phase.phase] || "divider",
                        }}
                      >
                        <CardContent
                          sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "text.secondary", fontWeight: 500 }}
                          >
                            {item.date}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 600,
                              color: "text.primary",
                              mb: 0.5,
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary" }}
                          >
                            {item.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
